/**
 * SMS Consent Checking Utilities
 * 
 * Functions to check if we can send SMS to a user based on their preferences
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

export type SMSPreference = 'all' | 'friends_only' | 'none';

export interface UserSMSPreferences {
  smsConsent?: boolean;
  smsPreference?: SMSPreference;
  smsOptOutDate?: any;
}

/**
 * Check if two users are friends
 */
export const areFriends = async (
  userId1: string,
  userId2: string
): Promise<boolean> => {
  if (userId1 === userId2) {
    return false; // Can't be friends with yourself
  }

  try {
    const pairId = [userId1, userId2].sort().join('_');
    const friendshipRef = doc(db, 'friendships', pairId);
    const friendship = await getDoc(friendshipRef);
    return friendship.exists();
  } catch (error) {
    console.error('Error checking friendship:', error);
    return false; // On error, assume not friends (safer)
  }
};

/**
 * Get user's SMS preferences
 */
export const getUserSMSPreferences = async (
  userId: string
): Promise<UserSMSPreferences | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return null;
    }

    const data = userDoc.data();
    return {
      smsConsent: data.smsConsent !== false, // Default to true if not set
      smsPreference: data.smsPreference || (data.smsConsent === false ? 'none' : 'all'),
      smsOptOutDate: data.smsOptOutDate,
    };
  } catch (error) {
    console.error('Error getting SMS preferences:', error);
    return null;
  }
};

/**
 * Check if we can send SMS to a user
 * 
 * @param recipientUserId - The user who will receive the SMS
 * @param senderUserId - The user who is sending the invite (optional, for friends_only check)
 * @returns true if we can send SMS, false otherwise
 */
export const canSendSMS = async (
  recipientUserId: string,
  senderUserId?: string
): Promise<boolean> => {
  try {
    const preferences = await getUserSMSPreferences(recipientUserId);
    
    if (!preferences) {
      // If user doesn't exist, default to allowing (for non-users)
      return true;
    }

    // Check if user has opted out completely
    if (preferences.smsPreference === 'none' || preferences.smsConsent === false) {
      console.log(`[SMS Consent] User ${recipientUserId} has opted out (preference: none)`);
      return false;
    }

    // Check if user only wants messages from friends
    if (preferences.smsPreference === 'friends_only') {
      if (!senderUserId) {
        // No sender specified, can't verify friendship - don't send
        console.log(`[SMS Consent] User ${recipientUserId} only accepts from friends, but no sender specified`);
        return false;
      }

      const isFriend = await areFriends(recipientUserId, senderUserId);
      if (!isFriend) {
        console.log(`[SMS Consent] User ${recipientUserId} only accepts from friends, and ${senderUserId} is not a friend`);
        return false;
      }
    }

    // All checks passed
    return true;
  } catch (error) {
    console.error('Error checking SMS consent:', error);
    // On error, default to not sending (safer for compliance)
    return false;
  }
};

